import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import { MdHistory } from 'react-icons/md'

import FuModalHeader from '@component/FollowUpFornecedor/ModalHeader'

import followUpFornecedorService from '@service/followUpFornecedor'

interface IHistoricoItem {
  id: number
  supplierId: number
  supplierDescription: string | null
  compactCategoryId: number | null
  compactCategoryDescription: string | null
  userId: number
  userLogin: string
  acao: 'CRIADO' | 'REMOVIDO'
  realizadoPorLogin: string
  ocorridoEm: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
}

const TAKE = 50

const acaoBadge = (acao: string) => {
  if (acao === 'CRIADO') {
    return (
      <Badge
        bg="green.600"
        color="white"
        borderRadius="md"
        px={2}
        fontSize="11px"
      >
        Criado
      </Badge>
    )
  }
  return (
    <Badge bg="red.500" color="white" borderRadius="md" px={2} fontSize="11px">
      Removido
    </Badge>
  )
}

const HistoricoResponsaveisModal: FC<Props> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [data, setData] = useState<IHistoricoItem[]>([])
  const [totalRows, setTotalRows] = useState(0)

  const fetchPage = async (skip: number, append: boolean) => {
    if (skip === 0) setLoading(true)
    else setLoadingMore(true)
    try {
      const { data: res } =
        await followUpFornecedorService.getHistoricoResponsaveis({
          take: TAKE,
          skip
        })
      const items: IHistoricoItem[] = res.data ?? []
      setData(prev => (append ? [...prev, ...items] : items))
      setTotalRows(res.pagination?.totalRows ?? 0)
    } catch {
      // silently ignore
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    setData([])
    fetchPage(0, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const hasMore = data.length < totalRows

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent>
        <FuModalHeader
          bg="#1d5840"
          icon={MdHistory}
          title="Histórico de Responsáveis"
          subtitle="Criações e remoções de vínculos por fornecedor"
        />
        <ModalCloseButton color="white" />
        <ModalBody bg="white" py={4} px={4}>
          {loading ? (
            <Flex justify="center" align="center" py={12}>
              <Spinner color="green.600" size="lg" />
            </Flex>
          ) : data.length === 0 ? (
            <Text color="gray.400" textAlign="center" py={8}>
              Nenhum registro encontrado.
            </Text>
          ) : (
            <Box>
              <Text fontSize="12px" color="gray.400" mb={3}>
                {totalRows} registro{totalRows !== 1 ? 's' : ''} no total
              </Text>
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="lg"
                overflow="hidden"
              >
                <Table size="sm">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th>Ação</Th>
                      <Th>Fornecedor</Th>
                      <Th>Categoria</Th>
                      <Th>Usuário</Th>
                      <Th>Realizado por</Th>
                      <Th>Data/Hora</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map(item => (
                      <Tr key={item.id}>
                        <Td>{acaoBadge(item.acao)}</Td>
                        <Td>
                          <Text fontSize="13px" fontWeight="500">
                            {item.supplierDescription ??
                              `ID ${item.supplierId}`}
                          </Text>
                          <Text fontSize="11px" color="gray.400">
                            ID {item.supplierId}
                          </Text>
                        </Td>
                        <Td fontSize="12px" color="gray.600">
                          {item.compactCategoryDescription ??
                            (item.compactCategoryId != null ? (
                              `ID ${item.compactCategoryId}`
                            ) : (
                              <Text as="span" color="gray.400">
                                Todos
                              </Text>
                            ))}
                        </Td>
                        <Td fontSize="12px">{item.userLogin}</Td>
                        <Td fontSize="12px" color="gray.500">
                          {item.realizadoPorLogin}
                        </Td>
                        <Td fontSize="12px" color="gray.400">
                          {new Date(item.ocorridoEm).toLocaleString('pt-BR')}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              {hasMore && (
                <Flex justify="center" mt={4}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    isLoading={loadingMore}
                    onClick={() => fetchPage(data.length, true)}
                  >
                    Carregar mais
                  </Button>
                </Flex>
              )}
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default HistoricoResponsaveisModal
